/**
 * React tree mounted inside the custom element's shadow root.
 *
 * Deliberately thin: it feeds the element's resolved config into
 * `useDocument()` and renders `DocumentViewer` with no callbacks, so the viewer
 * itself needs no changes. Clicks and selections are captured by
 * `DocumentWorkspaceElement` on the shadow root instead.
 */
import { memo, useCallback, useEffect } from 'react';

import { DocumentViewer } from '../components/DocumentViewer';
import { ERROR_CODES, EVENTS } from '../constants/element';
import { DocumentProvider, useDocument } from '../context/DocumentContext';

import { DocumentWorkspaceErrorBoundary } from './DocumentWorkspaceErrorBoundary';

import type { BlockRenderIssue } from '../renderer/contractRenderer';
import type { DocumentWorkspaceRootProps } from '../types/element';
import type { DocumentErrorEvent, DocumentReadyEvent } from '../types/events';

const DocumentWorkspaceBridge = memo(function DocumentWorkspaceBridge({
  contract,
  emit,
  highlightedBlockId,
  resolveHref,
}: DocumentWorkspaceRootProps) {
  const { highlightBlock, setContract } = useDocument();

  const handleRenderIssue = useCallback(
    (issues: BlockRenderIssue[]) => {
      for (const issue of issues) {
        emit<DocumentErrorEvent>(EVENTS.DOCUMENT_ERROR, {
          code: ERROR_CODES.BLOCK_RENDER_FAILURE,
          message: issue.message,
          context: {
            blockKey: issue.key,
            blockIndex: issue.index,
            blockType: issue.type,
            reason: issue.reason,
          },
        });
      }
    },
    [emit]
  );

  useEffect(() => {
    if (!contract) return;
    setContract(contract);
    emit<DocumentReadyEvent>(EVENTS.DOCUMENT_READY, {
      blockCount: contract.blocks.length,
      manuscriptId: contract.submissionId,
    });
  }, [contract, setContract, emit]);

  useEffect(() => {
    highlightBlock(highlightedBlockId);
  }, [highlightedBlockId, highlightBlock]);

  // Asset resolution belongs to the renderer: it understands s3:// hrefs and
  // renders a placeholder rather than a broken image when one cannot be mapped.
  return (
    <DocumentViewer
      resolveHref={resolveHref ?? undefined}
      onRenderIssue={handleRenderIssue}
    />
  );
});

export function DocumentWorkspaceRoot(props: DocumentWorkspaceRootProps) {
  return (
    <DocumentWorkspaceErrorBoundary emit={props.emit}>
      <DocumentProvider>
        <DocumentWorkspaceBridge {...props} />
      </DocumentProvider>
    </DocumentWorkspaceErrorBoundary>
  );
}
